import { convertEnergyPricePartnerToPowertracer } from 'src/util/conversions';
import { isFloatValueAndGTEQZero } from 'src/util/math';

/**
 * Handles a price <input> change event through a multi-step pipeline shared
 * across trade rule forms:
 * 1. Extracts the raw string value from the event.
 * 2. Updates the presentation state with the raw string to preserve user
 *    formatting (trailing zeros, decimals).
 * 3. Validates that the string is a non-negative float parsable by Big.js
 *    via isFloatValueAndGTEQZero.
 * 4. Converts a valid string into a Big.js value in Powertracer format
 *    ($/Wh) using convertEnergyPricePartnerToPowertracer.
 * 5. Validates the resulting Big.js value against trading limits using the
 *    supplied validator (e.g., isBuyPriceValid, isSellPriceValid). The
 *    validator is invoked as `validatePriceFunc(intlCurrency, priceBig)` so
 *    it receives the same locale context used for the conversion. Defaults
 *    to a permissive validator for contexts without stricter business
 *    limits than format validation.
 * 6. Updates the price state with the Big.js value (or null when the input
 *    is not a parsable float) and the validity state with the combined
 *    result of steps 3 and 5.
 * @param {object} options
 * @param {object} options.event - Input change event from the price field.
 * @param {object} options.intlCurrency - Currency-aware intl configuration
 *   used by convertEnergyPricePartnerToPowertracer and forwarded to
 *   validatePriceFunc.
 * @param {Function} options.setPriceFunc - State setter for the internal
 *   Big.js price value.
 * @param {Function} options.setPriceDisplayFunc - State setter for the raw
 *   display string shown in the input field.
 * @param {Function} options.setPriceValidFunc - State setter for the
 *   combined validity flag.
 * @param {Function} [options.validatePriceFunc] - Business-rule validator
 *   invoked as `(intlCurrency, priceBig) => boolean`. Defaults to
 *   `() => true`.
 * @returns {object|null} The resulting Big.js price in Powertracer format,
 *   or null when the raw input is not a valid non-negative float.
 */
const getPriceInputChangeResult = ({
  event,
  intlCurrency,
  setPriceFunc,
  setPriceDisplayFunc,
  setPriceValidFunc,
  validatePriceFunc = () => true,
}) => {
  const rawValue = event?.target?.value ?? '';
  setPriceDisplayFunc(rawValue);

  const hasValidFormat = isFloatValueAndGTEQZero(rawValue);
  const priceBig = hasValidFormat
    ? convertEnergyPricePartnerToPowertracer(intlCurrency, parseFloat(rawValue))
    : null;

  setPriceFunc(priceBig);
  setPriceValidFunc(hasValidFormat && validatePriceFunc(intlCurrency, priceBig));

  return priceBig;
};

export default getPriceInputChangeResult;
